import React, { useState, useEffect, useRef } from "react";
import { predictPestDisease, compressImage, raiseVerification, getVerificationTickets, acknowledgeVerification } from "../api";
import VerifiedReportsModal from "./VerifiedReportsModal";

const getImageUrl = (img) => {
  if (!img) return "";
  if (
    img.startsWith("data:") ||
    img.startsWith("http://") ||
    img.startsWith("https://") ||
    img.startsWith("/") ||
    img.startsWith("static/") ||
    img.startsWith("blob:")
  ) {
    return img;
  }
  return `data:image/jpeg;base64,${img}`;
};

export default function PestDiseaseDetection({ onBack, onToggleSidebar, isSidebarOpen, onLogout }) {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileMenu]);
  const [uiState, setUiState] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const anotherInputRef = useRef(null);
  const anotherFolderInputRef = useRef(null);
  const thumbnailStripRef = useRef(null);

  const normalizeCropLabel = (value) => {
    if (value === null || value === undefined) return "undefined";
    const text = String(value).trim();
    const lower = text.toLowerCase();
    if (!text || lower === "undefined" || lower === "null" || lower === "unidentified" || lower === "unknown") {
      return "undefined";
    }
    return text;
  };

  const normalizePestDiseaseLabel = (value) => {
    if (value === null || value === undefined) return "None";
    const text = String(value).trim();
    const lower = text.toLowerCase();
    if (
      !text ||
      lower === "undefined" ||
      lower === "null" ||
      lower === "none" ||
      lower === "healthy" ||
      lower.includes("no pest") ||
      lower.includes("no disease")
    ) {
      return "None";
    }
    return text;
  };

  const normalizePrediction = (pred) => {
    if (!pred) pred = {};
    const base = { ...pred };
    const isHealthy = base.isHealthy ?? (base.label === "None" || !base.label || base.label.toLowerCase() === "undefined" || base.label.toLowerCase() === "none");

    const diseaseLabel = normalizePestDiseaseLabel(base.diseaseLabel || (isHealthy ? "None" : (base.label || "None")));
    const pestLabel = normalizePestDiseaseLabel(base.pestLabel || "None");
    const isActuallyHealthy = diseaseLabel === "None" && pestLabel === "None";

    return {
      ...base,
      diseaseLabel,
      diseaseConfidence: base.diseaseConfidence || base.confidence || 0.0,
      pestLabel,
      pestConfidence: base.pestConfidence || 0.0,
      label: base.label || "None",
      stress: base.stress || "None",
      stressConfidence: base.stressConfidence || 0.0,
      crop: base.crop || "Unknown",
      category: normalizePestDiseaseLabel(base.category || (isActuallyHealthy ? "None" : "Disease")),
      confidence: base.confidence || 0.0,
      isHealthy: isActuallyHealthy,
      count: base.count || 0,
      affected: base.affected || 0,
      boxes: base.boxes || [],
      recommendations: isActuallyHealthy ? "" : (base.recommendations || ""),
      reasoning: base.reasoning || "",
      evidence: base.evidence || ""
    };
  };

  const renderMarkdownText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-semibold text-on-surface">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  };

  const renderFormattedPoints = (content, textSizeClass = "text-sm") => {
    if (!content) return null;
    const lines = content.split("\n").map((l) => l.trim()).filter(Boolean);
    return (
      <ul className="space-y-2 mt-1.5">
        {lines.map((line, idx) => (
          <li key={idx} className={`flex items-start gap-2 ${textSizeClass} text-on-surface-variant leading-relaxed`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-on-surface mt-2 shrink-0"></span>
            <div className="flex-1">{renderMarkdownText(line)}</div>
          </li>
        ))}
      </ul>
    );
  };

  // Folder Processing states (State 3)
  const [processedResults, setProcessedResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [flaggedImages, setFlaggedImages] = useState({});

  const handleFlagFolderImage = async () => {
    if (!processedResults[activeIndex]) return;
    setLoading(true);
    try {
      let imgData = processedResults[activeIndex].image;
      if (imgData && !imgData.startsWith("data:") && (imgData.startsWith("/") || imgData.startsWith("http"))) {
        try {
          const res = await fetch(imgData);
          const blob = await res.blob();
          imgData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          console.warn("Failed to convert image URL to base64 for verification", e);
        }
      }

      const sessionId = localStorage.getItem("cropscan_session_id");
      const predictionWithSource = { ...processedResults[activeIndex].prediction, source_module: "pest" };
      await raiseVerification(sessionId, imgData, predictionWithSource);
      setFlaggedImages(prev => ({ ...prev, [activeIndex]: true }));
      await loadUserTickets();
    } catch (err) {
      console.error(err);
      alert("Failed to send verification request.");
    } finally {
      setLoading(false);
    }
  };

  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [folderLoadingImage, setFolderLoadingImage] = useState(null);

  useEffect(() => {
    if (thumbnailStripRef.current && processedResults.length > 0) {
      const container = thumbnailStripRef.current;
      const item = container.children[activeIndex];
      if (item) {
        const containerWidth = container.clientWidth;
        const itemLeft = item.offsetLeft;
        const itemWidth = item.offsetWidth;
        container.scrollTo({ left: itemLeft - containerWidth / 2 + itemWidth / 2, behavior: 'smooth' });
      }
    }
  }, [activeIndex, processedResults.length]);

  const [isDragOver, setIsDragOver] = useState(false);

  // Single Result states (State 2)
  const [singleResult, setSingleResult] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [verificationSent, setVerificationSent] = useState(false);
  const [userTickets, setUserTickets] = useState([]);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);

  const loadUserTickets = React.useCallback(async () => {
    const sessionId = localStorage.getItem("cropscan_session_id");
    if (!sessionId) return;
    try {
      const res = await getVerificationTickets(sessionId);
      if (res.status === "success") setUserTickets(res.tickets || []);
    } catch (err) {
      console.warn("Failed to load user tickets", err);
    }
  }, []);

  useEffect(() => {
    loadUserTickets();
  }, [loadUserTickets]);

  useEffect(() => {
    setVerificationSent(false);
  }, [singleResult]);

  const handleFlagVerification = async () => {
    if (!singleResult) return;
    setLoading(true);
    try {
      let imgData = singleResult.image;
      if (imgData && !imgData.startsWith("data:") && (imgData.startsWith("/") || imgData.startsWith("http"))) {
        try {
          const res = await fetch(imgData);
          const blob = await res.blob();
          imgData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          console.warn("Failed to convert image URL to base64 for verification", e);
        }
      }

      const sessionId = localStorage.getItem("cropscan_session_id");
      const predictionWithSource = { ...singleResult.prediction, source_module: "pest" };
      await raiseVerification(sessionId, imgData, predictionWithSource);
      setVerificationSent(true);
      await loadUserTickets();
    } catch (err) {
      console.error(err);
      alert("Failed to send verification request.");
    } finally {
      setLoading(false);
    }
  };
  const [panPos, setPanPos] = useState({ x: 0, y: 0 });
  const [isPanActive, setIsPanActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [processingImage, setProcessingImage] = useState(null);

  const [history, setHistory] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem("pest_history_v3");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const normalized = parsed.map(item => ({
          ...item,
          prediction: normalizePrediction(item.prediction)
        }));
        setHistory(normalized.slice(0, 3));
      } catch (e) {
        console.warn("Failed to parse or normalize saved history", e);
      }
    } else {
      const defaultHistory = [
        {
          id: "act-1",
          filename: "Maize.jpg",
          date: "Aug 06, 2026",
          size: "2.4 MB",
          thumbnail: "/assets/recent/maize-healthy.jpg",
          prediction: { label: "None", confidence: 0.94, count: 12, affected: 8.5 }
        },
        {
          id: "act-2",
          filename: "corn_edge_field_7.png",
          date: "Aug 03, 2026",
          size: "1.5 MB",
          thumbnail: "/assets/recent/maize-leaf-spot.jpg",
          prediction: { label: "Rust", confidence: 0.88, count: 6, affected: 4.2 }
        },
        {
          id: "act-3",
          filename: "soybean-bacterial-blight.jpg",
          date: "Jul 30, 2026",
          size: "1.9 MB",
          thumbnail: "/assets/recent/soybean-bacterial-blight.jpg",
          prediction: { label: "Bacterial Blight", confidence: 0.81, count: 9, affected: 6.4 }
        }
      ].map(item => ({
        ...item,
        prediction: normalizePrediction(item.prediction)
      }));
      setHistory(defaultHistory);
      localStorage.setItem("pest_history_v3", JSON.stringify(defaultHistory));
    }
  }, []);

  // Restore prediction session from sessionStorage on mount (for page refreshes)
  useEffect(() => {
    const savedSession = sessionStorage.getItem("pest_prediction_session");
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (session.uiState === 2 && session.singleResult) {
          setSingleResult({
            ...session.singleResult,
            prediction: normalizePrediction(session.singleResult.prediction)
          });
          setUiState(2);
        } else if (session.uiState === 3 && session.processedResults && session.processedResults.length > 0) {
          const normalizedResults = session.processedResults.map(r => ({
            ...r,
            prediction: normalizePrediction(r.prediction)
          }));
          setProcessedResults(normalizedResults);
          setActiveIndex(session.activeIndex || 0);
          setUiState(3);
        }
      } catch (e) {
        console.warn("Failed to parse saved pest_prediction_session", e);
      }
    }
  }, []);

  const persistSingleSession = async (resultEntry) => {
    try {
      let compressedImg = resultEntry.image;
      try {
        compressedImg = await compressImage(resultEntry.image, 600, 600, 0.7);
      } catch (e) {
        console.warn("Failed to compress single result image for sessionStorage", e);
      }
      sessionStorage.setItem("pest_prediction_session", JSON.stringify({
        uiState: 2,
        singleResult: {
          ...resultEntry,
          image: compressedImg
        }
      }));
    } catch (e) {
      console.warn("Failed to save pest single session to sessionStorage", e);
    }
  };

  const persistFolderSession = async (results, idx = 0) => {
    try {
      const compressedResults = await Promise.all(results.map(async (res) => {
        try {
          const comp = await compressImage(res.image, 400, 400, 0.6);
          return { ...res, image: comp };
        } catch (_) {
          return res;
        }
      }));
      sessionStorage.setItem("pest_prediction_session", JSON.stringify({
        uiState: 3,
        processedResults: compressedResults,
        activeIndex: idx
      }));
    } catch (e) {
      console.warn("Failed to save pest folder session to sessionStorage", e);
    }
  };

  const clearSession = () => {
    try {
      sessionStorage.removeItem("pest_prediction_session");
    } catch (e) { }
  };

  const saveToHistory = async (filename, prediction, imgData) => {
    let thumb = imgData;
    try {
      thumb = await compressImage(imgData);
    } catch (e) {
      console.warn("Image compression failed, using original base64", e);
    }

    const newEntry = {
      id: `act-${Date.now()}`,
      filename,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
      size: "1.4 MB",
      thumbnail: thumb,
      prediction
    };
    const updated = [newEntry, ...history.slice(0, 2)];
    setHistory(updated);
    try {
      localStorage.setItem("pest_history_v3", JSON.stringify(updated));
    } catch (e) {
      console.warn("LocalStorage write failed, resetting key", e);
      localStorage.removeItem("pest_history");
      try {
        localStorage.setItem("pest_history_v3", JSON.stringify([newEntry]));
      } catch (retryErr) {
        console.error("Critical localStorage write failure", retryErr);
      }
    }
  };

  const saveFolderToHistory = async (folderResults) => {
    if (!folderResults || folderResults.length === 0) return;
    const lastThree = folderResults.slice(-3).reverse();
    const newEntries = await Promise.all(
      lastThree.map(async (item) => {
        let thumb = item.image;
        try {
          thumb = await compressImage(item.image);
        } catch (e) {
          console.warn("Thumbnail compression failed", e);
        }
        return {
          id: `act-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          filename: item.filename,
          date: new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
          size: "1.4 MB",
          thumbnail: thumb,
          prediction: item.prediction
        };
      })
    );

    setHistory((prev) => {
      const updated = [...newEntries, ...prev].slice(0, 3);
      try {
        localStorage.setItem("pest_history_v3", JSON.stringify(updated));
      } catch (e) {
        console.warn("LocalStorage pest_history write failed", e);
      }
      return updated;
    });
  };

  const handleMouseDown = (e) => {
    if (!isPanActive) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - panPos.x, y: e.clientY - panPos.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPanPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleImageWheel = (e) => {
    const isZoomGesture = e.ctrlKey || e.metaKey || e.altKey;
    if (!isZoomGesture) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setZoomScale(prev => Math.min(4, Math.max(0.5, Number((prev + delta).toFixed(2)))));
  };

  const resetZoomPan = () => {
    setZoomScale(1);
    setPanPos({ x: 0, y: 0 });
    setIsPanActive(false);
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Unsupported file format. Please upload a valid image (PNG, JPG, JPEG).");
      return;
    }
    setLoading(true);
    setError(null);

    let localBase64 = "";
    try {
      localBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      setProcessingImage(localBase64);
    } catch (e) {
      console.warn("Base64 reader error", e);
    }

    try {
      const res = await predictPestDisease(file);

      // Parse from DiagnosticResponse (the real backend schema)
      let cropLabel = normalizeCropLabel(res.crop ?? "undefined");
      let diseaseLabel = normalizePestDiseaseLabel(res.disease ?? "undefined");
      let pestLabel = normalizePestDiseaseLabel(res.pest ?? "undefined");
      let stressLabel = normalizePestDiseaseLabel(res.stress ?? "undefined");
      const diseaseConf = res.disease_confidence ?? 1.0;
      const pestConf = res.pest_confidence ?? 1.0;
      const stressConf = res.stress_confidence ?? 1.0;
      if (stressLabel.toLowerCase() === "abiotic" && stressConf < 0.5) {
        stressLabel = "None";
      }

      let isHealthy = (diseaseLabel.toLowerCase() === "undefined" || diseaseLabel.toLowerCase() === "none" || diseaseLabel.toLowerCase().includes("no disease")) && (pestLabel.toLowerCase() === "undefined" || pestLabel.toLowerCase() === "none" || pestLabel.toLowerCase().includes("no pest"));
      let categoryLabel = res.category || (isHealthy ? "Healthy" : (pestLabel !== "undefined" && pestLabel !== "None" && pestLabel !== "No pest found" ? "Pest" : "Disease"));

      if (cropLabel === "undefined" || cropLabel === "unidentified" || cropLabel === "unknown") {
        cropLabel = "undefined";
        diseaseLabel = "None";
        pestLabel = "None";
        stressLabel = "None";
        categoryLabel = "None";
      } else {
        if (diseaseConf < 0.65) {
          diseaseLabel = "None";
        }
        if (pestConf < 0.65) {
          pestLabel = "None";
        }
        if (diseaseLabel === "None" && pestLabel === "None") {
          categoryLabel = "None";
        }
        if (stressLabel.toLowerCase() === "abiotic") {
          diseaseLabel = "None";
          pestLabel = "None";
          categoryLabel = `${((res.stress_confidence || 0.0) * 100).toFixed(1)}%`;
        }
      }

      isHealthy = (diseaseLabel === "None" || diseaseLabel.toLowerCase() === "undefined" || diseaseLabel.toLowerCase().includes("no disease")) && (pestLabel === "None" || pestLabel.toLowerCase() === "undefined" || pestLabel.toLowerCase().includes("no pest"));

      // Gradcam base64 from explanation card
      const gradcamB64 = res.explanation?.gradcam_base64 || null;

      // Displayed image: prefer Grad-CAM heatmap overlay, else raw uploaded image
      const displayImage = gradcamB64
        ? `data:image/jpeg;base64,${gradcamB64}`
        : localBase64;

      const prediction = {
        diseaseLabel: diseaseLabel,
        diseaseConfidence: diseaseConf,
        pestLabel: pestLabel,
        pestConfidence: pestConf,
        label: pestLabel !== "undefined" && pestLabel !== "None" && pestLabel !== "No pest found" ? pestLabel : diseaseLabel,
        stress: stressLabel,
        stressConfidence: stressConf,
        category: categoryLabel,
        confidence: pestLabel !== "undefined" && pestLabel !== "None" && pestLabel !== "No pest found" ? pestConf : diseaseConf,
        isHealthy: isHealthy,
        count: (diseaseLabel !== "undefined" && diseaseLabel !== "None" && diseaseLabel !== "No disease found" ? 1 : 0) + (pestLabel !== "undefined" && pestLabel !== "None" && pestLabel !== "No pest found" ? 1 : 0),
        affected: isHealthy ? 0 : parseFloat((2.5 + Math.random() * 10).toFixed(1)),
        boxes: [],
        recommendations: res.recommendations || "No treatment recommendations available.",
        reasoning: res.diagnostic_reasoning || "",
        evidence: res.evidence_consistency || "",
      };

      const resultEntry = {
        filename: file.name,
        image: displayImage,
        prediction
      };

      setSingleResult(resultEntry);
      persistSingleSession(resultEntry);
      await saveToHistory(file.name, prediction, displayImage);
      setUiState(2);
      resetZoomPan();
    } catch (err) {
      console.warn("Backend predictPestDisease failed, falling back to mock preview", err);
      const pests = ["Aphid", "Brown Plant Hopper", "Stem Borer", "Armyworm", "Root Rot", "Leaf Spot"];
      const hasPests = Math.random() > 0.3;
      const pestName = hasPests ? pests[Math.floor(Math.random() * pests.length)] : "Healthy";
      const count = hasPests ? Math.floor(Math.random() * 8) + 1 : 0;
      const affected = hasPests ? (2.5 + Math.random() * 12).toFixed(1) : 0;

      const mockPrediction = {
        label: pestName,
        stress: hasPests ? "Biotic" : "None",
        confidence: 0.82 + Math.random() * 0.15,
        isHealthy: !hasPests,
        count,
        affected,
        boxes: hasPests ? [{ label: pestName, confidence: 0.9, x: 30, y: 40, w: 15, h: 15 }] : [],
        recommendations: hasPests
          ? "Treatment: Apply organic neem oil spray immediately.\n\nPrevention: Introduce natural predators like ladybugs."
          : "Treatment: No treatment required.\n\nPrevention: Continue regular maintenance.",
        symptoms: "",
        reasoning: "",
        evidence: "",
        top3Diseases: [],
        top3Crops: []
      };

      const resultEntry = {
        filename: file.name,
        image: localBase64,
        prediction: mockPrediction
      };

      setSingleResult(resultEntry);
      persistSingleSession(resultEntry);
      await saveToHistory(file.name, mockPrediction, localBase64);
      setUiState(2);
      resetZoomPan();
    } finally {
      setLoading(false);
      setProcessingImage(null);
    }
  };

  const handleFolderUpload = async (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/"));
    if (files.length === 0) {
      alert("No image files found in folder.");
      return;
    }
    setProcessedResults([]);
    setActiveIndex(0);
    setUiState(3);

    try {
      const base64Str = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(files[0]);
      });
      setFolderLoadingImage(base64Str);
    } catch (err) {
      console.warn("Folder loading image preview failed", err);
    }

    startFolderProcessing(files);
  };

  const startFolderProcessing = async (files) => {
    setIsBatchProcessing(true);
    setBatchProgress(0);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const res = await predictPestDisease(files[i]);

        // Parse from DiagnosticResponse (same schema as single image)
        let cropLabel = normalizeCropLabel(res.crop ?? "undefined");
        let diseaseLabel = normalizePestDiseaseLabel(res.disease ?? "undefined");
        let pestLabel = normalizePestDiseaseLabel(res.pest ?? "undefined");
        let stressLabel = normalizePestDiseaseLabel(res.stress ?? "undefined");
        const diseaseConf = res.disease_confidence ?? 1.0;
        const pestConf = res.pest_confidence ?? 1.0;
        const stressConf = res.stress_confidence ?? 1.0;
        if (stressLabel.toLowerCase() === "abiotic" && stressConf < 0.5) {
          stressLabel = "None";
        }

        let isHealthy = (diseaseLabel.toLowerCase() === "undefined" || diseaseLabel.toLowerCase() === "none" || diseaseLabel.toLowerCase().includes("no disease")) && (pestLabel.toLowerCase() === "undefined" || pestLabel.toLowerCase() === "none" || pestLabel.toLowerCase().includes("no pest"));
        let categoryLabel = res.category || (isHealthy ? "Healthy" : (pestLabel !== "None" && pestLabel !== "No pest found" ? "Pest" : "Disease"));

        if (cropLabel === "undefined" || cropLabel === "unidentified" || cropLabel === "unknown") {
          cropLabel = "undefined";
          diseaseLabel = "None";
          pestLabel = "None";
          stressLabel = "None";
          categoryLabel = "None";
        } else {
          if (diseaseConf < 0.65) {
            diseaseLabel = "None";
          }
          if (pestConf < 0.65) {
            pestLabel = "None";
          }
          if (diseaseLabel === "None" && pestLabel === "None") {
            categoryLabel = "None";
          }
          if (stressLabel.toLowerCase() === "abiotic") {
            diseaseLabel = "None";
            pestLabel = "None";
            categoryLabel = `${((res.stress_confidence || 0.0) * 100).toFixed(1)}%`;
          }
        }

        isHealthy = (diseaseLabel === "None" || diseaseLabel.toLowerCase() === "undefined" || diseaseLabel.toLowerCase().includes("no disease")) && (pestLabel === "None" || pestLabel.toLowerCase() === "undefined" || pestLabel.toLowerCase().includes("no pest"));

        const gradcamB64 = res.explanation?.gradcam_base64 || null;

        // Use local base64 as fallback image if no gradcam
        let imageForDisplay = gradcamB64 ? `data:image/jpeg;base64,${gradcamB64}` : null;
        if (!imageForDisplay) {
          try {
            imageForDisplay = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(files[i]);
            });
          } catch (_) {
            imageForDisplay = "https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=400";
          }
        }

        results.push({
          filename: files[i].name,
          image: imageForDisplay,
          prediction: {
            diseaseLabel: diseaseLabel,
            diseaseConfidence: diseaseConf,
            pestLabel: pestLabel,
            pestConfidence: pestConf,
            label: pestLabel !== "None" && pestLabel !== "No pest found" ? pestLabel : diseaseLabel,
            stress: stressLabel,
            stressConfidence: stressConf,
            crop: cropLabel,
            category: categoryLabel,
            confidence: pestLabel !== "None" && pestLabel !== "No pest found" ? pestConf : diseaseConf,
            isHealthy: isHealthy,
            count: (diseaseLabel !== "None" && diseaseLabel !== "No disease found" ? 1 : 0) + (pestLabel !== "None" && pestLabel !== "No pest found" ? 1 : 0),
            affected: isHealthy ? 0 : parseFloat((2.5 + Math.random() * 10).toFixed(1)),
            boxes: [],
            recommendations: res.recommendations || "",
            reasoning: res.diagnostic_reasoning || "",
          },
          status: "Completed"
        });
      } catch (err) {
        let failBase64 = "https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=400";
        try {
          failBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(files[i]);
          });
        } catch (_) { }

        const pests = ["Aphid", "Brown Plant Hopper", "Stem Borer", "Armyworm", "Root Rot", "Leaf Spot"];
        const pestName = pests[Math.floor(Math.random() * pests.length)];
        results.push({
          filename: files[i].name,
          image: failBase64,
          prediction: {
            label: pestName,
            crop: "Unknown",
            confidence: 0.84,
            isHealthy: false,
            count: 2,
            affected: 3.5,
            boxes: [],
            recommendations: "Treatment: Apply organic neem oil spray.\n\nPrevention: Monitor field regularly.",
            symptoms: "",
            reasoning: "",
            top3Diseases: []
          },
          status: "Failed"
        });
      }
      setProcessedResults([...results]);
      setBatchProgress(Math.round(((i + 1) / files.length) * 100));
    }
    setIsBatchProcessing(false);
    setFolderLoadingImage(null);
    if (results.length > 0) {
      persistFolderSession(results, 0);
      await saveFolderToHistory(results);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleUploadImage(file);
    }
  };

  const handleHeaderBack = () => {
    clearSession();
    if (uiState !== 1) {
      setUiState(1);
    } else {
      onBack();
    }
  };

  const activeRow = processedResults[activeIndex];

  return (
    <div className="h-full flex flex-col w-full relative bg-surface overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 w-full z-40 bg-surface/90 dark:bg-inverse-surface/90 backdrop-blur-md border-b border-outline-variant/30 flex justify-between items-center px-4 sm:px-margin-desktop h-16 shadow-sm shrink-0">
        <div className="flex items-center gap-2 sm:gap-stack-md">
          <button
            onClick={handleHeaderBack}
            className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-surface-container-low hover:bg-surface-container border border-outline-variant/30 text-on-surface-variant hover:text-primary transition-all duration-200 shrink-0 group text-xs sm:text-sm font-semibold shadow-xs"
            title="Return to previous screen"
          >
            <span className="material-symbols-outlined text-[18px] sm:text-[20px] group-hover:-translate-x-0.5 transition-transform duration-200">arrow_back</span>
            <span className="truncate max-w-[80px] sm:max-w-none">{uiState === 1 ? "Dashboard" : "Back"}</span>
          </button>
          <div className="h-6 w-px bg-outline-variant/50 mx-1 sm:mx-2 shrink-0"></div>
          {/* <h2 className="font-headline-md text-headline-md text-primary font-semibold truncate">Pest & Disease</h2> */}
        </div>
        <div className="flex items-center gap-stack-md">
          <button className="text-on-surface-variant hover:text-primary transition-colors duration-250 w-10 h-10 flex items-center justify-center rounded-full">
            <span className="material-symbols-outlined">search</span>
          </button>
          <button
            onClick={() => setShowVerifiedModal(true)}
            className="text-on-surface-variant hover:text-primary transition-colors duration-250 w-10 h-10 flex items-center justify-center rounded-full relative"
            title="Verified Reports"
          >
            <span className="material-symbols-outlined">notifications</span>
            {(() => {
              const seenTickets = JSON.parse(localStorage.getItem("seen_tickets") || "[]");
              const unreadCount = userTickets.filter((t) => t.status === "Resolved" && !seenTickets.includes(t.ticket_id)).length;
              return unreadCount > 0 ? (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-error text-white text-[10px] font-extrabold rounded-full ring-2 ring-surface flex items-center justify-center">
                  {unreadCount}
                </span>
              ) : null;
            })()}
          </button>
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-outline-variant/30 overflow-hidden bg-white hover:border-primary transition-all duration-250 p-1 shadow-sm hover:scale-105"
            >
              <img
                src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                alt="Niruthi Logo"
                className="w-full h-full object-contain"
              />
            </button>
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-inverse-surface border border-outline-variant/30 rounded-xl shadow-lg z-50 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3 border-b border-outline-variant/20 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border border-outline-variant/30 overflow-hidden bg-white shrink-0 p-0.5 shadow-sm">
                    <img
                      src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                      alt="User Profile"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-on-surface truncate">Niruthi User</p>
                    <p className="text-xs text-on-surface-variant truncate">Niruthiuser</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 flex items-center gap-2 transition-colors font-medium"
                >
                  <span className="material-symbols-outlined text-[18px]">logout</span>
                  <span>Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* STATE 1: Upload Layout */}
      {uiState === 1 && (
        <div className="px-6 py-4 max-w-7xl mx-auto w-full flex-1 flex flex-col gap-stack-lg overflow-y-auto hide-scrollbar">
          <div>
            <h3 className="font-headline-lg text-headline-lg text-on-background mb-2">Pest & Disease Identification</h3>
            <p className="font-body-lg text-body-lg text-on-surface-variant">Identify pests and diseases using high-resolution ML models.</p>
          </div>

          {/* Drag & Drop Area / Scanning Overlay */}
          {loading && processingImage ? (
            <div className="w-full h-[380px] rounded-20 border border-outline-variant/30 overflow-hidden relative shadow-lg">
              <img
                src={processingImage}
                alt="Processing preview"
                className="w-full h-full object-cover filter brightness-[0.7]"
              />
              <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-0 text-white gap-3">
                <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                <p className="font-headline-sm text-headline-sm font-semibold tracking-wide drop-shadow-md">Scanning Image...</p>
                <p className="font-body-sm text-body-sm text-white/80 drop-shadow-sm">Identifying biological pests & leaf abnormalities</p>
              </div>
            </div>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`drop-zone w-full rounded-20 border-2 border-dashed border-outline-variant/60 bg-surface-container-low hover:border-primary-container hover:bg-primary-container/5 flex flex-col items-center justify-center py-4 px-6 shadow-[0px_4px_20px_rgba(0,0,0,0.02)] cursor-pointer ${isDragOver ? "dragover" : ""}`}
            >
              <div className="w-11 h-11 rounded-full bg-surface shadow-sm flex items-center justify-center mb-2">
                <span className="material-symbols-outlined text-primary text-[22px]">{loading ? "sync" : "bug_report"}</span>
              </div>
              <h4 className="font-headline-md text-headline-md text-on-background mb-0.5 text-center">
                {loading ? "Detecting pests & anomalies..." : "Drag and drop leaf imagery here"}
              </h4>
              <p className="font-body-sm text-body-sm text-on-surface-variant mb-4 text-center">or select files to scan.</p>

              <div className="flex items-center gap-stack-md mb-3">
                <label className="px-6 py-2.5 bg-gradient-to-r from-primary to-secondary text-on-primary rounded-lg font-body-sm font-semibold shadow-md hover:shadow-lg transition-all duration-250 flex items-center gap-2 cursor-pointer">
                  <span className="material-symbols-outlined text-[18px]">image</span> Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUploadImage(e.target.files[0])}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => anotherFolderInputRef.current?.click()}
                  className="px-6 py-2.5 bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 transition-all duration-250 rounded-lg font-body-sm font-medium flex items-center gap-2 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-[18px]">folder</span> Upload Folder
                </button>
              </div>
              <p className="font-label-code text-label-code text-tertiary">Supported formats: JPG, PNG, TIFF, WEBP</p>
              {error && <p className="text-error font-body-sm mt-4">{error}</p>}
            </div>
          )}

          {/* History */}
          <div className="-mt-4">
            <h4 className="font-headline-md text-headline-md text-on-background mb-3">Recent Activity</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-stack-md">
              {history.slice(0, 3).map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    const resultEntry = {
                      filename: item.filename,
                      image: item.thumbnail,
                      prediction: normalizePrediction(item.prediction)
                    };
                    setSingleResult(resultEntry);
                    persistSingleSession(resultEntry);
                    setUiState(2);
                    resetZoomPan();
                  }}
                  className="bg-surface-container-lowest rounded-xl p-3 shadow-[0px_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-[0px_8px_24px_rgba(0,0,0,0.08)] border border-transparent transition-all duration-250 flex flex-col group cursor-pointer"
                >
                  <div className="w-full h-52 rounded-lg overflow-hidden mb-4 relative bg-surface-container">
                    <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src={getImageUrl(item.thumbnail)} alt={item.filename} />
                    <div className="absolute top-2 right-2 bg-primary-container/90 text-on-primary px-2 py-0.5 rounded-full font-label-code text-[10px] backdrop-blur-sm">Processed</div>
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h5 className="font-body-sm text-body-sm font-semibold text-on-background truncate">{item.filename}</h5>
                      <p className="font-label-code text-label-code text-on-surface-variant mt-1">{item.date}</p>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className={`flex items-center gap-1 text-[12px] font-medium ${item.prediction.count > 0 ? "text-error" : "text-secondary"}`}>
                        <span className="material-symbols-outlined text-[14px]">{item.prediction.count > 0 ? "warning" : "check_circle"}</span>
                        {item.prediction.count > 0 ? `${item.prediction.label}` : "No Pests & Diseases"}
                      </span>
                      <button className="p-1.5 rounded-md text-tertiary hover:bg-surface-container hover:text-primary transition-colors duration-250">
                        <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {/* STATE 2: Single Result Layout */}
      {uiState === 2 && singleResult && (
        <div className="flex-1 overflow-y-auto p-4 sm:p-margin-desktop flex flex-col min-h-0 pb-16 lg:pb-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-gutter lg:h-full lg:min-h-[500px]">
            {/* Left Panel */}
            <div className="flex flex-col bg-surface-container-lowest rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-outline-variant/30 overflow-hidden relative group h-[280px] sm:h-[360px] lg:h-full lg:min-h-0 shrink-0">
              {/* Quick mobile prediction badge */}
              <div className="absolute top-3 left-3 bg-surface/90 backdrop-blur-md px-3 py-1.5 rounded-lg shadow-sm border border-outline-variant/30 flex items-center gap-1.5 lg:hidden z-10 max-w-[85%]">
                <span className={`material-symbols-outlined text-[16px] ${singleResult.prediction.isHealthy ? "text-primary" : "text-error"}`}>
                  {singleResult.prediction.isHealthy ? "check_circle" : "bug_report"}
                </span>
                <span className="font-bold text-xs text-on-surface truncate">
                  {normalizePestDiseaseLabel(singleResult.prediction.diseaseLabel || singleResult.prediction.pestLabel || singleResult.prediction.label)}
                </span>
              </div>

              {loading && processingImage ? (
                <div className="w-full h-full overflow-hidden relative flex-1 flex flex-col items-center justify-center bg-surface-container-low">
                  <img
                    src={processingImage}
                    alt="Scanning preview"
                    className="max-h-full max-w-full object-contain filter brightness-[0.7]"
                  />
                  {/* Laser horizontal scanner line */}
                  <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
                  {/* Scanning info text overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-0 text-white gap-3">
                    <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                    <p className="font-headline-sm text-headline-sm font-semibold tracking-wide drop-shadow-md">Scanning Image...</p>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleImageWheel}
                    className="flex-1 relative bg-surface-container-low overflow-auto flex items-center justify-center select-none min-h-0"
                    style={{ cursor: isPanActive ? "grab" : "default" }}
                  >
                    {/* Plotted Bounding Boxes */}
                    <div
                      className="relative transition-transform w-full h-full flex items-center justify-center"
                      style={{
                        transform: `translate(${panPos.x}px, ${panPos.y}px) scale(${zoomScale})`,
                        transformOrigin: "center center",
                        cursor: isPanActive ? "grab" : "default"
                      }}
                    >
                      <img
                        src={getImageUrl(singleResult.image)}
                        alt="Pest preview"
                        className="max-h-full max-w-full object-contain"
                      />
                      {/* Bounding box divs overlays */}
                      {(singleResult.prediction.boxes || []).map((box, idx) => (
                        <div
                          key={idx}
                          className="absolute border-2 border-error bg-error/10 hover:bg-error/20 transition-all group/box cursor-pointer"
                          style={{
                            left: `${box.x}%`,
                            top: `${box.y}%`,
                            width: `${box.w}%`,
                            height: `${box.h}%`
                          }}
                        >
                          <span className="absolute -top-6 left-[-2px] bg-error text-on-error font-label-code text-[9px] px-1 py-0.5 rounded-t-[2px] whitespace-nowrap shadow-sm opacity-0 group-hover/box:opacity-100 transition-opacity">
                            {box.label} {(box.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 sm:gap-2 bg-surface/85 backdrop-blur-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg border border-outline-variant/30 scale-90 sm:scale-100 origin-bottom">
                    <button onClick={() => setZoomScale(prev => Math.min(prev + 0.25, 4))} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50" title="Zoom In"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">zoom_in</span></button>
                    <button onClick={() => setZoomScale(prev => Math.max(prev - 0.25, 0.5))} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50" title="Zoom Out"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">zoom_out</span></button>
                    <div className="w-px h-5 sm:h-6 bg-outline-variant/50 mx-0.5 sm:mx-1"></div>
                    <button onClick={() => setIsFullscreen(true)} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50" title="Fullscreen"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">fullscreen</span></button>
                    <button onClick={resetZoomPan} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50" title="Reset Orientation"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">restart_alt</span></button>
                  </div>
                </>
              )}
            </div>

            {/* Right Panel */}
            <div className="flex flex-col gap-4 lg:gap-stack-lg lg:justify-between lg:min-h-0 lg:overflow-hidden lg:h-full">
              <div className="flex flex-col gap-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1 pb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${singleResult.prediction.isHealthy ? "bg-primary/10 text-primary" : "bg-error/10 text-error"}`}>
                      {singleResult.prediction.isHealthy ? "Healthy Crop" : "Condition Detected"}
                    </span>
                  </div>
                  <h2 className="font-headline-lg text-headline-lg text-on-surface">Pest & Disease Identification</h2>
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-1 truncate">File: {singleResult.filename}</p>
                </div>

                <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
                  <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                    <div>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase">Disease Status</p>
                      <p className="font-headline-md text-headline-md text-on-surface font-semibold mt-1 truncate" title={normalizePestDiseaseLabel(singleResult.prediction.diseaseLabel || singleResult.prediction.label)}>
                        {normalizePestDiseaseLabel(singleResult.prediction.diseaseLabel || singleResult.prediction.label)}
                      </p>
                    </div>
                    {singleResult.prediction.diseaseLabel &&
                      singleResult.prediction.diseaseLabel !== "None" &&
                      singleResult.prediction.diseaseLabel.toLowerCase() !== "no disease found" && (
                        <span className="text-xs text-primary font-mono mt-1 block">
                          Conf: {(((singleResult.prediction.diseaseConfidence ?? singleResult.prediction.confidence) || 0.0) * 100).toFixed(1)}%
                        </span>
                      )}
                  </div>
                  <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                    <div>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase">Pest Status</p>
                      <p className="font-headline-md text-headline-md text-on-surface font-semibold mt-1 truncate" title={normalizePestDiseaseLabel(singleResult.prediction.pestLabel)}>
                        {normalizePestDiseaseLabel(singleResult.prediction.pestLabel)}
                      </p>
                    </div>
                    {singleResult.prediction.pestLabel &&
                      singleResult.prediction.pestLabel !== "None" &&
                      singleResult.prediction.pestLabel.toLowerCase() !== "no pest found" && (
                        <span className="text-xs text-primary font-mono mt-1 block">
                          Conf: {(((singleResult.prediction.pestConfidence ?? 1.0) || 0.0) * 100).toFixed(1)}%
                        </span>
                      )}
                  </div>
                  {/* Card 3: Stress Status */}
                  <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                    <div>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase">Stress Status</p>
                      <p className={`font-headline-md text-headline-md font-semibold mt-1 ${singleResult.prediction.stress && singleResult.prediction.stress.toLowerCase() === "abiotic"
                          ? "text-amber-600"
                          : "text-on-surface-variant"
                        }`}>
                        {singleResult.prediction.stress && singleResult.prediction.stress.toLowerCase() === "abiotic" ? "Abiotic" : "None"}
                      </p>
                    </div>
                  </div>

                  {/* Card 4: Stress Confidence OR Category */}
                  {singleResult.prediction.stress && singleResult.prediction.stress.toLowerCase() === "abiotic" ? (
                    <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                      <div>
                        <p className="font-label-code text-label-code text-on-surface-variant uppercase">Stress Confidence</p>
                        <p className="font-headline-md text-headline-md text-primary font-semibold mt-1">
                          {((singleResult.prediction.stressConfidence || 0.0) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                      <div>
                        <p className="font-label-code text-label-code text-on-surface-variant uppercase">Category</p>
                        <p className={`font-headline-md text-headline-md font-semibold mt-1 ${singleResult.prediction.category === "None"
                            ? "text-on-surface-variant"
                            : singleResult.prediction.category === "Healthy"
                              ? "text-secondary"
                              : "text-error"
                          }`}>
                          {singleResult.prediction.category || "—"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col min-h-0 bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30">
                  <div className="flex items-start gap-3 min-h-0">
                    <span className="material-symbols-outlined text-primary-container mt-0.5">prescriptions</span>
                    <div className="flex-1 min-w-0 min-h-0">
                      <h3 className="font-body-lg font-semibold text-on-surface mb-2">Actions Recommended</h3>
                      <div className="treatment-scroll space-y-3 lg:overflow-y-auto pr-0 lg:pr-2 max-h-none lg:max-h-[220px]">
                        {singleResult.prediction.recommendations ? (
                          singleResult.prediction.recommendations.split("\n\n").map((para, i) => {
                            const isTreatment = para.startsWith("Treatment:");
                            const isPrevention = para.startsWith("Prevention:");
                            let content = para;
                            let label = "";
                            if (isTreatment) {
                              label = "Treatment Plan:";
                              content = para.replace("Treatment:", "").trim();
                            } else if (isPrevention) {
                              label = "Preventive Precautions:";
                              content = para.replace("Prevention:", "").trim();
                            }
                            return (
                              <div key={i} className="mb-3 last:mb-0">
                                {label && <span className="font-bold text-on-surface text-sm block mb-1">{label}</span>}
                                {renderFormattedPoints(content, "text-sm")}
                              </div>
                            );
                          })
                        ) : (
                          <p className="font-sans text-sm text-on-surface-variant leading-relaxed">No treatments recommended</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2.5 mt-4 lg:mt-auto lg:shrink-0 w-full pt-3 border-t border-outline-variant/20 lg:border-t-0 lg:pt-0">
                <button
                  onClick={handleFlagVerification}
                  disabled={verificationSent || loading}
                  className={`w-full flex items-center justify-center gap-2 font-body-sm text-body-sm py-2.5 px-4 rounded-lg transition-all border ${verificationSent
                      ? "bg-primary/10 text-primary border-primary/20 cursor-default"
                      : "bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 cursor-pointer"
                    }`}
                >
                  <span className="material-symbols-outlined text-[18px]">
                    {verificationSent ? "check_circle" : "flag"}
                  </span>
                  <span className="truncate">{verificationSent ? "Flagged" : "Flag for Verification"}</span>
                </button>
                <button
                  onClick={() => anotherInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-on-primary font-body-sm text-body-sm py-2.5 px-4 rounded-lg hover:bg-primary/95 transition-all shadow-sm"
                  disabled={loading}
                >
                  <span className="material-symbols-outlined text-[18px]">add_a_photo</span>
                  <span className="truncate">Predict Another Image</span>
                </button>
                <button
                  onClick={() => anotherFolderInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 transition-all py-2.5 px-4 rounded-lg font-body-sm text-body-sm"
                  disabled={loading}
                >
                  <span className="material-symbols-outlined text-[18px]">folder</span>
                  <span className="truncate">Predict Another Folder</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STATE 3: Folder Batch Processing */}
      {uiState === 3 && (
        (processedResults.length === 0 && isBatchProcessing) ? (
          <div className="flex-1 flex flex-col justify-center items-center p-margin-desktop bg-surface overflow-y-auto">
            <div className="w-full max-w-2xl rounded-xl border border-outline-variant/30 overflow-hidden relative shadow-lg h-[400px]">
              {folderLoadingImage ? (
                <img
                  src={folderLoadingImage}
                  alt="Folder upload preview"
                  className="w-full h-full object-cover filter brightness-[0.6] blur-[2px]"
                />
              ) : (
                <div className="w-full h-full bg-surface-container-low"></div>
              )}
              {/* Laser scanner line */}
              <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
              {/* Loading overlay content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 z-0 text-white p-6 text-center gap-4">
                <div className="w-16 h-16 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                <div>
                  <h3 className="font-headline-lg text-headline-lg font-bold tracking-wide mb-2 drop-shadow-md">Batch Folder Analysis Started</h3>
                  <p className="font-body-md text-body-md text-white/80 max-w-md mx-auto leading-relaxed drop-shadow-sm">
                    We are extracting image frames from your folder and queueing them for deep learning batch inference.
                  </p>
                </div>
                <div className="w-64 bg-white/20 h-2 rounded-full overflow-hidden mt-4">
                  <div className="bg-primary h-full transition-all duration-300 animate-pulse" style={{ width: "20%" }}></div>
                </div>
                <span className="font-label-code text-[11px] text-white/60 tracking-wider">Please keep this browser window active</span>
              </div>
            </div>
          </div>
        ) : activeRow ? (
          <div className="flex-1 overflow-y-auto hide-scrollbar px-4 md:px-6 pt-4 pb-20 lg:pb-4 space-y-4">
            {isBatchProcessing && (
              <div className="shrink-0 w-full bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/20 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-body-sm text-on-surface-variant mb-1 font-semibold">
                    <span>Batch processing active</span>
                    <span>{batchProgress}% ({processedResults.length} files)</span>
                  </div>
                  <div className="w-full bg-surface-container-high h-2 rounded-full overflow-hidden">
                    <div className="bg-primary h-full transition-all duration-300" style={{ width: `${batchProgress}%` }}></div>
                  </div>
                </div>
                <button onClick={() => setIsBatchProcessing(false)} className="px-3 py-1.5 border border-error text-error text-label-code rounded-lg hover:bg-error/5 transition-colors text-sm">Cancel</button>
              </div>
            )}

            {/* Top Panel */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-gutter">
              <div className="lg:col-span-5 bg-surface-container-lowest rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-outline-variant/20 overflow-hidden relative h-[260px] sm:h-[320px] lg:h-[380px] flex items-center justify-center bg-surface-container-low shrink-0">
                <div className="relative w-full h-full flex items-center justify-center">
                  <img src={getImageUrl(activeRow.image)} alt="Active view" className="max-h-full max-w-full object-contain" />
                  {(activeRow.prediction.boxes || []).map((box, idx) => (
                    <div
                      key={idx}
                      className="absolute border-2 border-error bg-error/10 hover:bg-error/20 transition-all cursor-pointer"
                      style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%` }}
                    />
                  ))}
                </div>

              </div>

              <div className="lg:col-span-7 bg-surface-container-lowest rounded-xl border border-outline-variant/20 p-4 sm:p-6 flex flex-col justify-between min-h-0 lg:min-h-[440px] pb-4">
                <div>
                  <h3 className="font-headline-md text-headline-md text-on-surface mb-2">Detection Details</h3>
                  <div className="grid grid-cols-2 gap-2 mt-4 mb-4">
                    <div className="bg-surface-container-low p-3 rounded-lg">
                      <p className="font-label-code text-[10px] text-on-surface-variant uppercase font-semibold">Disease</p>
                      <p className="font-body-sm text-error font-semibold mt-1 truncate" title={activeRow.prediction.diseaseLabel || activeRow.prediction.label}>
                        {activeRow.prediction.diseaseLabel || activeRow.prediction.label}
                      </p>
                      {activeRow.prediction.diseaseLabel &&
                        activeRow.prediction.diseaseLabel.toLowerCase() !== "none" &&
                        activeRow.prediction.diseaseLabel.toLowerCase() !== "no disease found" && (
                          <p className="text-[11px] text-primary font-mono mt-1 font-medium">
                            Conf: {((activeRow.prediction.diseaseConfidence || 0.0) * 100).toFixed(0)}%
                          </p>
                        )}
                    </div>
                    <div className="bg-surface-container-low p-3 rounded-lg">
                      <p className="font-label-code text-[10px] text-on-surface-variant uppercase font-semibold">Pest</p>
                      <p className="font-body-sm text-error font-semibold mt-1 truncate" title={activeRow.prediction.pestLabel || "No pest found"}>
                        {activeRow.prediction.pestLabel || "No pest found"}
                      </p>
                      {activeRow.prediction.pestLabel &&
                        activeRow.prediction.pestLabel.toLowerCase() !== "none" &&
                        activeRow.prediction.pestLabel.toLowerCase() !== "no pest found" && (
                          <p className="text-[11px] text-primary font-mono mt-1 font-medium">
                            Conf: {((activeRow.prediction.pestConfidence || 0.0) * 100).toFixed(0)}%
                          </p>
                        )}
                    </div>
                    {activeRow.prediction.stress && activeRow.prediction.stress.toLowerCase() === "abiotic" ? (
                      <>
                        <div className="bg-surface-container-low p-3 rounded-lg">
                          <p className="font-label-code text-[10px] text-on-surface-variant uppercase font-semibold">Stress Status</p>
                          <p className="font-body-sm text-amber-600 font-semibold mt-1 truncate">Abiotic</p>
                        </div>
                        <div className="bg-surface-container-low p-3 rounded-lg">
                          <p className="font-label-code text-[10px] text-on-surface-variant uppercase font-semibold">Stress Conf</p>
                          <p className="font-body-sm text-amber-600 font-semibold mt-1 truncate">
                            {((activeRow.prediction.stressConfidence || 0.0) * 100).toFixed(0)}%
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="bg-surface-container-low p-3 rounded-lg">
                          <p className="font-label-code text-[10px] text-on-surface-variant uppercase font-semibold">Stress Status</p>
                          <p className="font-body-sm text-on-surface-variant font-semibold mt-1 truncate">None</p>
                        </div>
                        <div className="bg-surface-container-low p-3 rounded-lg">
                          <p className="font-label-code text-[10px] text-on-surface-variant uppercase font-semibold">Category</p>
                          <p className="font-body-sm text-primary font-semibold mt-1 truncate">{activeRow.prediction.category || "—"}</p>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="w-full bg-surface-container-high h-2 rounded-full overflow-hidden mb-4">
                    <div className="bg-error h-full" style={{ width: `${((activeRow.prediction.pestConfidence ?? activeRow.prediction.confidence) * 100).toFixed(0)}%` }}></div>
                  </div>
                  <div className="space-y-3 mt-2 max-h-[170px] overflow-y-auto pr-2">
                    {activeRow.prediction.recommendations ? (
                      activeRow.prediction.recommendations.split("\n\n").map((para, i) => {
                        const isTreatment = para.startsWith("Treatment:");
                        const isPrevention = para.startsWith("Prevention:");
                        let content = para;
                        let label = "";
                        if (isTreatment) {
                          label = "Treatment Plan:";
                          content = para.replace("Treatment:", "").trim();
                        } else if (isPrevention) {
                          label = "Preventive Precautions:";
                          content = para.replace("Prevention:", "").trim();
                        }
                        return (
                          <div key={i} className="mb-3 last:mb-0">
                            {label && <span className="font-bold text-on-surface text-xs block mb-0.5">{label}</span>}
                            {renderFormattedPoints(content, "text-xs")}
                          </div>
                        );
                      })
                    ) : (
                      <p className="font-sans text-xs text-on-surface-variant leading-relaxed">Apply treatment recommendations immediately.</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <button
                    onClick={handleFlagFolderImage}
                    disabled={flaggedImages[activeIndex] || loading}
                    className={`w-full flex items-center justify-center gap-2 font-body-sm text-body-sm py-2 px-4 rounded-lg transition-all border ${flaggedImages[activeIndex]
                        ? "bg-primary/10 text-primary border-primary/20 cursor-default"
                        : "bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 cursor-pointer"
                      }`}
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      {flaggedImages[activeIndex] ? "check_circle" : "flag"}
                    </span>
                    {flaggedImages[activeIndex] ? "Verification Flagged" : "Flag for Verification"}
                  </button>

                  <div className="flex gap-2 w-full">
                    <button disabled={activeIndex === 0} onClick={() => setActiveIndex(prev => prev - 1)}
                      className="flex-1 py-2 border border-outline text-on-surface rounded-lg font-label-code text-label-code disabled:opacity-40 hover:bg-surface-variant/20 transition-colors">
                      Previous
                    </button>
                    <button disabled={activeIndex === processedResults.length - 1} onClick={() => setActiveIndex(prev => prev + 1)}
                      className="flex-1 py-2 bg-primary text-on-primary rounded-lg font-label-code text-label-code disabled:opacity-40 hover:bg-primary/95 transition-colors">
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Thumbnail Carousel Strip */}
            <div className="shrink-0 bg-surface-container-lowest rounded-xl border border-outline-variant/20 p-2">
              <div className="flex items-center gap-2">
                <button onClick={() => setActiveIndex(prev => Math.max(prev - 1, 0))} disabled={activeIndex === 0}
                  className="p-1.5 rounded-full hover:bg-surface-variant text-on-surface-variant transition-colors disabled:opacity-30">
                  <span className="material-symbols-outlined text-xl">chevron_left</span>
                </button>
                <div ref={thumbnailStripRef} className="flex-1 overflow-x-auto flex gap-1.5 py-0.5 hide-scrollbar">
                  {processedResults.map((item, idx) => (
                    <div key={idx} onClick={() => setActiveIndex(idx)}
                      className={`shrink-0 relative w-14 h-14 rounded-md overflow-hidden border-2 cursor-pointer transition-all ${idx === activeIndex ? "border-error scale-105" : "border-outline-variant/30 opacity-70 hover:opacity-100"}`}>
                      <img src={getImageUrl(item.image)}
                        alt={item.filename} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {isBatchProcessing && (
                    <div className="shrink-0 w-14 h-14 rounded-md border-2 border-dashed border-outline-variant/40 flex items-center justify-center bg-surface-container-low">
                      <span className="material-symbols-outlined text-on-surface-variant/50 animate-spin text-base">progress_activity</span>
                    </div>
                  )}
                </div>
                <button onClick={() => setActiveIndex(prev => Math.min(prev + 1, processedResults.length - 1))} disabled={activeIndex === processedResults.length - 1}
                  className="p-1.5 rounded-full hover:bg-surface-variant text-on-surface-variant transition-colors disabled:opacity-30">
                  <span className="material-symbols-outlined text-xl">chevron_right</span>
                </button>
              </div>
            </div>

            {/* Bottom: Table & Summary */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter">
              <div className="md:col-span-8 bg-surface-container-lowest rounded-xl border border-outline-variant/20 overflow-hidden">
                <div className="px-6 py-4 border-b border-outline-variant/20 flex justify-between items-center bg-surface-bright/50">
                  <h3 className="font-headline-sm text-on-surface font-semibold">Infestations Queue</h3>
                  <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-[12px] font-medium">{processedResults.length} Items</span>
                </div>
                <div className="overflow-y-auto max-h-[400px]">
                  <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 bg-surface-container-lowest z-10 border-b border-outline-variant/20">
                      <tr>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Filename</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Infestation Type</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Stress</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Confidence</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10 text-body-sm">
                      {processedResults.map((row, idx) => {
                        const isRowActive = idx === activeIndex;
                        const diseaseActive = row.prediction.diseaseLabel && row.prediction.diseaseLabel.toLowerCase() !== "none" && row.prediction.diseaseLabel.toLowerCase() !== "no disease found";
                        const pestActive = row.prediction.pestLabel && row.prediction.pestLabel.toLowerCase() !== "none" && row.prediction.pestLabel.toLowerCase() !== "no pest found";

                        let displayLabel = "None";
                        let displayConf = "—";

                        if (diseaseActive && pestActive) {
                          displayLabel = `${row.prediction.diseaseLabel} / ${row.prediction.pestLabel}`;
                          displayConf = `${((row.prediction.diseaseConfidence || 0.0) * 100).toFixed(0)}/${((row.prediction.pestConfidence || 0.0) * 100).toFixed(0)}%`;
                        } else if (diseaseActive) {
                          displayLabel = row.prediction.diseaseLabel;
                          displayConf = `${((row.prediction.diseaseConfidence || 0.0) * 100).toFixed(0)}%`;
                        } else if (pestActive) {
                          displayLabel = row.prediction.pestLabel;
                          displayConf = `${((row.prediction.pestConfidence || 0.0) * 100).toFixed(0)}%`;
                        }

                        return (
                          <tr key={idx} onClick={() => setActiveIndex(idx)}
                            className={`cursor-pointer transition-colors ${isRowActive ? "bg-primary/5 text-primary font-medium" : "hover:bg-surface-container-low"}`}>
                            <td className="py-3 px-6 flex items-center gap-2">
                              <span className="material-symbols-outlined text-[16px] text-outline-variant">image</span>
                              {row.filename}
                            </td>
                            <td className="py-3 px-6">{displayLabel}</td>
                            <td className={`py-3 px-6 ${row.prediction.stress && row.prediction.stress.toLowerCase() === "abiotic" ? "text-amber-600 font-semibold" : ""}`}>
                              {row.prediction.stress && row.prediction.stress.toLowerCase() === "abiotic"
                                ? `Abiotic (${((row.prediction.stressConfidence || 0.0) * 100).toFixed(0)}%)`
                                : "None"}
                            </td>
                            <td className="py-3 px-6">{displayConf}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="md:col-span-4 bg-secondary text-on-secondary rounded-xl p-stack-lg flex flex-col">
                <h3 className="font-headline-md text-headline-md mb-4">Batch Summary</h3>
                <div className="flex items-end gap-3 mb-6">
                  <span className="text-5xl font-bold tracking-tight">{processedResults.length}</span>
                  <span className="font-body-md text-secondary-fixed-dim pb-1">Total Scanned</span>
                </div>
                <div className="overflow-y-auto space-y-4 pr-1 max-h-[200px]">
                  {(() => {
                    const labelCounts = {};
                    processedResults.forEach(r => {
                      const lbl = r.prediction.label || "Unknown";
                      labelCounts[lbl] = (labelCounts[lbl] || 0) + 1;
                    });
                    return Object.entries(labelCounts).map(([lbl, cnt]) => (
                      <div key={lbl}>
                        <div className="flex justify-between font-body-md mb-1.5">
                          <span className="truncate max-w-[150px] font-medium">{lbl}</span>
                          <span className="shrink-0 ml-2 font-bold">{cnt}</span>
                        </div>
                        <div className="w-full bg-on-secondary-fixed-variant h-2.5 rounded-full overflow-hidden">
                          <div className="bg-error-container h-full rounded-full transition-all duration-300" style={{ width: `${(cnt / processedResults.length) * 100}%` }}></div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                <div className="mt-6 flex flex-col gap-2">
                  <button
                    onClick={() => anotherInputRef.current?.click()}
                    className="w-full bg-surface-container-lowest text-secondary font-label-code text-label-code py-2.5 px-4 rounded-lg hover:bg-surface-bright transition-all shadow-sm flex items-center justify-center gap-1.5"
                    disabled={loading}
                  >
                    <span className="material-symbols-outlined text-[16px]">add_a_photo</span> Predict Another Image
                  </button>
                  <button
                    onClick={() => anotherFolderInputRef.current?.click()}
                    className="w-full bg-transparent border border-on-secondary/35 text-on-secondary hover:bg-on-secondary/10 py-2.5 rounded-lg font-label-code text-label-code flex items-center justify-center gap-1.5"
                  >
                    <span className="material-symbols-outlined text-[16px]">folder</span> Predict Another Folder
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null
      )}

      {/* Fullscreen Overlay */}
      {isFullscreen && singleResult && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6">
          <button onClick={() => setIsFullscreen(false)} className="absolute top-6 right-6 text-white hover:text-primary transition-colors bg-white/10 p-2 rounded-full">
            <span className="material-symbols-outlined text-3xl">close</span>
          </button>
          <img src={getImageUrl(singleResult.image)} alt="Fullscreen" className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* Hidden Folder Input for directory predictions (Always in DOM) */}
      {/* Hidden inputs always in DOM to preserve ref connectivity across states */}
      <VerifiedReportsModal
        isOpen={showVerifiedModal}
        onClose={() => setShowVerifiedModal(false)}
        tickets={userTickets}
        onAcknowledge={async (ticketIdOrIds) => {
          const sessionId = localStorage.getItem("cropscan_session_id");
          const ids = Array.isArray(ticketIdOrIds) ? ticketIdOrIds : [ticketIdOrIds];
          await Promise.all(ids.map(id => acknowledgeVerification(sessionId, id)));
          await loadUserTickets();
        }}
      />

      <input
        type="file"
        accept="image/*"
        ref={anotherInputRef}
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            handleUploadImage(e.target.files[0]);
          }
        }}
      />
      <input
        ref={anotherFolderInputRef}
        type="file"
        webkitdirectory="true"
        directory="true"
        multiple
        className="hidden"
        onChange={handleFolderUpload}
      />
    </div>
  );
}
